import type { CSSProperties } from 'react';

/**
 * Chat answer-item citation chips: superscript number in a grey rounded pill.
 * Inline styles are included so copy/paste (and non-Tailwind surfaces) keep the look.
 */

export const CITATION_SUP_CLASSNAME =
  'citation-sup-badge text-white bg-[#2C3A4A] text-[9px] leading-[14px] rounded-full ml-0.5 min-w-[14px] min-h-[14px] px-[3px] inline-block align-middle text-center whitespace-nowrap';

export const CITATION_SUP_CLASSNAME_LIGHT =
  'citation-sup-badge text-black bg-[#dddddd] text-[9px] leading-[14px] rounded-full ml-0.5 min-w-[14px] min-h-[14px] px-[3px] inline-block align-middle text-center whitespace-nowrap';

export const CITATION_SUP_STYLE: CSSProperties = {
  backgroundColor: '#2C3A4A',
  color: '#ffffff',
  fontSize: '9px',
  lineHeight: '14px',
  borderRadius: '9999px',
  marginLeft: '2px',
  minWidth: '14px',
  minHeight: '14px',
  paddingLeft: '3px',
  paddingRight: '3px',
  display: 'inline-block',
  verticalAlign: 'middle',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

export const CITATION_SUP_STYLE_LIGHT: CSSProperties = {
  ...CITATION_SUP_STYLE,
  backgroundColor: '#dddddd',
  color: '#000000',
};

/** Inline style attribute for clipboard HTML (no React). */
export const CITATION_SUP_STYLE_ATTR = Object.entries(CITATION_SUP_STYLE)
  .map(([key, value]) => {
    const cssKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
    return `${cssKey}:${value}`;
  })
  .join(';');

/**
 * Ensure every ``<sup>`` in HTML carries the citation-badge inline style so
 * paste targets (Word, email, MDX) keep the grey pill even without our CSS.
 */
export function ensureCitationSupInlineStyles(html: string): string {
  if (!html || !/<sup\b/i.test(html)) return html;
  return html.replace(/<sup(\s[^>]*)?>/gi, (_match, rawAttrs: string | undefined) => {
    const attrs = rawAttrs ?? '';
    if (/\sstyle\s*=/i.test(attrs)) {
      // Merge our badge styles ahead of any existing style so they win on conflict.
      return `<sup${attrs.replace(
        /\sstyle\s*=\s*(["'])([\s\S]*?)\1/i,
        (_m, quote: string, existing: string) =>
          ` style=${quote}${CITATION_SUP_STYLE_ATTR};${existing}${quote}`,
      )}>`;
    }
    const classAttr = /\sclass\s*=/i.test(attrs)
      ? attrs
      : `${attrs} class="citation-sup-badge"`;
    return `<sup${classAttr} style="${CITATION_SUP_STYLE_ATTR}">`;
  });
}
